import { useParams, useNavigate } from 'react-router-dom'
import {
  Container, Stack, Title, Text, Paper, Group, Button, Loader, Center, Badge, Box,
} from '@mantine/core'
import { IconArrowLeft, IconRefresh, IconUsers } from '@tabler/icons-react'
import { useTeamAnalysis } from '../hooks/useTeamAnalysis'
import { TeamGroupDynamicsView } from '../components/teams/TeamGroupDynamicsView'
import { TeamComparisonView } from '../components/teams/TeamComparisonView'
import { MarkdownRenderer } from '../components/common/MarkdownRenderer'

export default function TeamAnalysis() {
  const { id, teamStoryId } = useParams()
  const navigate = useNavigate()
  const { analysis, isLoading, isProcessing, error, refetch } = useTeamAnalysis(id, teamStoryId)

  const back = (
    <Button
      variant="subtle"
      color="gray"
      size="sm"
      leftSection={<IconArrowLeft size={16} />}
      onClick={() => navigate(`/teams/${id}`)}
      style={{ alignSelf: 'flex-start' }}
    >
      Back to Team
    </Button>
  )

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          {back}
          <Center py={60}><Loader color="cyan" /></Center>
        </Stack>
      </Container>
    )
  }

  if (isProcessing) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          {back}
          <Stack align="center" gap="md" py={60}>
            <Loader color="cyan" />
            <Text fw={600} ff="Orbitron, sans-serif" c="cyan.3">
              Generating Team Analysis
            </Text>
            <Text c="dimmed" size="sm" ta="center" maw={420}>
              Aggregating responses from across the team. This may take a minute.
            </Text>
            <Button leftSection={<IconRefresh size={14} />} variant="light" color="cyan" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Container>
    )
  }

  if (error || !analysis) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          {back}
          <Stack align="center" gap="md" py={60}>
            <Text c="red.4" fw={600} ff="Orbitron, sans-serif">
              Analysis Unavailable
            </Text>
            <Text c="dimmed" size="sm" ta="center" maw={420}>
              {error?.response?.data?.error || 'Unable to load team analysis. Try again shortly.'}
            </Text>
            <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>Retry</Button>
          </Stack>
        </Stack>
      </Container>
    )
  }

  const facilitatorView = !!analysis.facilitator_view

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {back}

        <Paper p="lg" className="glass" style={{ borderColor: 'rgba(0, 229, 255, 0.2)', textAlign: 'center' }}>
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={2} mb={4}>
            Team Analysis
          </Text>
          <Text size="xl" fw={700} ff="Orbitron, sans-serif" c="cyan.3" className="glow-text-cyan">
            {analysis.story_name || 'Story'}
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            {analysis.team_name}
          </Text>
          <Group gap="sm" justify="center" mt="xs">
            {analysis.member_count != null && (
              <Badge size="sm" color="cyan" variant="light" leftSection={<IconUsers size={10} />}>
                {analysis.member_count} member{analysis.member_count === 1 ? '' : 's'}
              </Badge>
            )}
            {analysis.completed_at && (
              <Text size="xs" c="dimmed">
                Completed {new Date(analysis.completed_at).toLocaleDateString()}
              </Text>
            )}
            {facilitatorView && (
              <Badge size="sm" color="cyan" variant="light">Facilitator view</Badge>
            )}
          </Group>
        </Paper>

        {analysis.member_summary && (
          <Paper p="lg" className="glass">
            <Box c="gray.3" style={{ fontSize: 14, lineHeight: 1.6 }}>
              <MarkdownRenderer content={analysis.member_summary} />
            </Box>
          </Paper>
        )}

        <TeamGroupDynamicsView dynamics={analysis.group_dynamics} />

        <TeamComparisonView comparison={analysis.comparison} facilitatorView={facilitatorView} />

        {analysis.notes && (
          <Box>
            <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1} mb="xs">
              Notes
            </Text>
            <Paper p="lg" className="glass">
              <Box c="gray.3" style={{ fontSize: 14, lineHeight: 1.6 }}>
                <MarkdownRenderer content={analysis.notes} />
              </Box>
            </Paper>
          </Box>
        )}
      </Stack>
    </Container>
  )
}
